import {
  Button,
  Dropdown,
  Input,
  MenuProps,
  Space,
  Table,
  TablePaginationConfig,
} from 'antd';
import { useForm } from 'antd/es/form/Form';
import useModal from 'antd/es/modal/useModal';
import { ProviderForm } from './provider-form.component';
import { debounce, map, uniq } from 'lodash';
import { useCallback } from 'react';
import {
  COMMERCIAL_TERMS,
  GAMBLING_DISCOUNT_TYPE,
  PROVIDER_STATUS,
  PROVIDER_STATUS_LABEL,
} from '../constants/provider-form.types';
import { PublishMessageForm } from './publish-message-form.component';
import { Message } from '../utils/api-client';
import { Link, useNavigate } from 'react-router-dom';
import { useProviderTypes } from '../utils/api-hooks/provider.hook';

type Data = {
  address: string | null;
  addressComment: string | null;
  id: string;
  idNumber: string;
  lineGroupId: string;
  name: string;
  nickname: string;
  taxId: string | null;
  type: string | null;
  status: PROVIDER_STATUS;
  needPosSticker: boolean;
  businessInfo: {
    chargeSlottingFee: boolean;
    commercialTerms: COMMERCIAL_TERMS | null;
    commission: number | null;
    gamblingRules: Array<{
      itemSoldQuantityGuarantee: number;
      discountType: GAMBLING_DISCOUNT_TYPE;
      discountAmount: number;
    }> | null;
  } | null;
  paymentInfo: {
    account: string | null;
    accountName: string | null;
    bankName: string | null;
    bankBranchName: string | null;
    receiptType: string | null;
    shouldInvoice: boolean;
  } | null;
  tags: {
    id: string;
    name: string;
  }[];
};

export function ProvidersComponent({
  data,
  isLoading,
  pagination,
  setKeyword,
  createProvider,
  deleteProvider,
  updateProvider,
  todayDeliveryMeals,
  sendProviderPublishMessage,
  setFilters,
}: {
  data: Data[];
  isLoading: boolean;
  pagination?: TablePaginationConfig;
  setKeyword: (keyword: string) => void;
  createProvider: (data: { name: string; lineGroupId: string }) => Promise<any>;
  deleteProvider: (id: string) => Promise<any>;
  setFilters: (filter: Record<string, any>) => void;
  updateProvider: (data: {
    id: string;
    idNumber: string;
    lineGroupId: string;
    name: string;
  }) => Promise<any>;
  todayDeliveryMeals: Array<{
    provider: schedules.Provider;
  }>;
  sendProviderPublishMessage: (data: {
    providerIds?: string[];
    messages: Array<Message>;
  }) => Promise<any>;
}) {
  const [modal, contextHolder] = useModal();
  const [form] = useForm();
  const [publishMessageForm] = useForm();
  const navigate = useNavigate();
  const { data: { data: providerTypes } = {} } = useProviderTypes();

  const onCreateButtonClick = () => {
    modal.confirm({
      cancelText: '取消',
      content: <ProviderForm form={form} />,
      icon: null,
      okText: '確認',
      title: '新增供應商',
      width: 640,
      onOk: async () => {
        const values = await form.validateFields();

        return createProvider(values).then((res) => {
          navigate(`${res.data?.data?.id}`);
        });
      },
    });
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const onKeywordChange = useCallback(
    debounce((e: React.ChangeEvent<HTMLInputElement>) => {
      setKeyword(e.target.value);
    }, 500),
    []
  );

  const dropdownMenus: MenuProps['items'] = [
    {
      key: 'all',
      onClick: () => {
        modal.confirm({
          content: <PublishMessageForm form={publishMessageForm} />,
          icon: null,
          title: '發送Line推播',
          width: 640,
          onOk: async () => {
            const values = await publishMessageForm.validateFields();

            return sendProviderPublishMessage({
              messages: values.messages,
            });
          },
        });
      },
      label: '全部供應商',
    },
    {
      key: 'special',
      onClick: () => {
        modal.confirm({
          icon: null,
          title: '發送Line推播',
          width: 640,
          content: (
            <PublishMessageForm form={publishMessageForm} needProvider />
          ),
          onOk: async () => {
            const values = await publishMessageForm.validateFields();

            return sendProviderPublishMessage({
              providerIds: values.providerIds,
              messages: values.messages,
            });
          },
        });
      },
      label: '特定供應商',
    },
    {
      key: 'todayMeals',
      onClick: () => {
        modal.confirm({
          content: <PublishMessageForm form={publishMessageForm} />,
          icon: null,
          title: '發送Line推播',
          width: 640,
          onOk: async () => {
            const values = await publishMessageForm.validateFields();

            return sendProviderPublishMessage({
              providerIds: uniq(map(todayDeliveryMeals, 'provider.id')),
              messages: values.messages,
            });
          },
        });
      },
      label: '今日發餐的供應商',
    },
  ];

  return (
    <Space direction="vertical" className="w-full">
      <div className="text-2xl font-bold">供應商列表</div>
      {contextHolder}
      <Table
        dataSource={data}
        loading={isLoading}
        pagination={pagination}
        rowKey="id"
        size="small"
        scroll={{ x: 'max-content' }}
        onChange={(pagination, filters) => {
          setFilters(filters);
        }}
        columns={[
          {
            align: 'center',
            width: 80,
            title: '編號',
            dataIndex: 'idNumber',
          },
          {
            title: '合作狀態',
            dataIndex: 'status',
            width: 100,
            filters: [
              {
                text: PROVIDER_STATUS_LABEL[PROVIDER_STATUS.ACTIVE],
                value: PROVIDER_STATUS.ACTIVE,
              },
              {
                text: PROVIDER_STATUS_LABEL[PROVIDER_STATUS.INACTIVE],
                value: PROVIDER_STATUS.INACTIVE,
              },
            ],
            onFilter: (value, record) => record.status === value,
            render: (status) => {
              const color = status === PROVIDER_STATUS.ACTIVE ? 'green' : 'red';
              return (
                <span style={{ color }}>
                  {PROVIDER_STATUS_LABEL[status as PROVIDER_STATUS] || '---'}
                </span>
              );
            },
          },
          {
            title: 'POS單貼紙',
            dataIndex: 'needPosSticker',
            width: 120,
            filters: [
              {
                text: '有貼紙',
                value: true,
              },
              {
                text: '無貼紙',
                value: false,
              },
            ],
            onFilter: (value, record) => record.needPosSticker === value,
            render: (needPosSticker) => {
              const color = needPosSticker === true ? 'green' : '';
              console.log(needPosSticker);
              return (
                <span style={{ color }}>{needPosSticker ? '有貼紙' : '-'}</span>
              );
            },
          },
          {
            title: '簡稱',
            width: 100,
            dataIndex: 'nickname',
            render(value, record, index) {
              return <Link to={`${record.id}`}>{value}</Link>;
            },
          },
          {
            align: 'center',
            title: '合作條件',
            width: 80,
            dataIndex: ['businessInfo', 'commission'],
            render(value) {
              return value ? `${value}%` : '---';
            },
          },
          {
            title: '類型',
            dataIndex: 'types',
            width: 100,
            filters: map(providerTypes, (type) => {
              return {
                text: type.name,
                value: type.name,
              };
            }),
            render: (types, record) => {
              return record.type;
            },
          },
          {
            title: '名稱',
            dataIndex: 'name',
            width: 200,
            render(value, record, index) {
              return <Link to={`${record.id}`}>{value}</Link>;
            },
          },
          {
            title: '線上訂單',
            dataIndex: 'orderLink',
            width: 80,
            render(value, record, index) {
              return (
                <a
                  href={`${process.env.REACT_APP_PUBLIC_PAGE_URL}/provider/${record.id}`}
                >
                  訂單連結
                </a>
              );
            },
          },
          {
            title: '標籤',
            dataIndex: 'tags',
            render: (tags) => {
              return (
                <div className="flex flex-wrap gap-2">
                  {map(tags, (tag) => {
                    return (
                      <div key={tag.id} className="px-2 bg-gray-200 rounded">
                        {tag.name}
                      </div>
                    );
                  })}
                </div>
              );
            },
          },
          {
            title: '操作',
            dataIndex: 'id',
            width: 130,
            render: (id, record) => {
              return (
                <Space>
                  <Button
                    type="primary"
                    size="small"
                    onClick={() => {
                      navigate(`${id}`);
                    }}
                  >
                    編輯
                  </Button>
                  <Button
                    size="small"
                    type="primary"
                    danger
                    onClick={() => {
                      modal.confirm({
                        icon: null,
                        title: '刪除供應商',
                        okText: '確認',
                        cancelText: '取消',
                        content: '確定要刪除供應商嗎？',
                        onOk: async () => {
                          return deleteProvider(id);
                        },
                      });
                    }}
                  >
                    刪除
                  </Button>
                </Space>
              );
            },
          },
        ]}
        title={() => {
          return (
            <div className="flex flex-col items-start gap-2">
              <div className="flex flex-row items-start gap-2">
                <Button type="primary" onClick={onCreateButtonClick}>
                  新增供應商
                </Button>
                <Dropdown
                  menu={{ items: dropdownMenus }}
                  placement="bottomLeft"
                >
                  <Button type="primary">Line 推播</Button>
                </Dropdown>
              </div>

              <div className="flex flex-row items-start gap-2">
                <div className="flex items-center gap-4">
                  <span className="shrink-0">關鍵字</span>
                  <Input
                    placeholder="輸入關鍵字搜尋名稱"
                    onChange={onKeywordChange}
                  />
                </div>
              </div>
            </div>
          );
        }}
      />
    </Space>
  );
}
